/**
 * Consolidated hash-navigation controller.
 *
 * Supersedes `deep-link.js` + `anchorScrollHandler.js`. Reads the URL hash
 * once, reveals only what's needed (expand accordion/tab/collapse, wait for
 * async `mhecomm-*` components), then performs a single corrective scroll +
 * focus move.
 *
 * Key design decisions:
 * - Supplement, don't replace: activation (expanding an accordion/tab) only
 *   happens if the target actually needs revealing. But the final scroll
 *   position is always verified and corrected if wrong -- native
 *   scroll-to-fragment is NOT guaranteed to run on every hash-bearing load.
 *   On reload, the browser's scroll RESTORATION (`history.scrollRestoration`,
 *   `"auto"` by default) can restore the pre-reload scroll position instead
 *   of scrolling to the fragment at all, even when nothing needed revealing.
 * - Two corrective-scroll passes: one right after activation settles, and a
 *   second on `window.load` to catch layout shifts from late-loading content
 *   (e.g. images) inside an already-activated pane. Both respect the same
 *   user-interaction guard.
 * - For plain Bootstrap-activated content (a real chain was activated), the
 *   scroll waits for the target's position to stop changing across a few
 *   checks first -- `shown.bs.*` only signals the shell opened, not that
 *   content inside it has finished rendering. `mhecomm-*` web components
 *   already have their own `whenReady()` contract and skip this extra wait.
 * - Uses `bootstrap.Collapse`/`bootstrap.Tab` APIs instead of `.click()` to
 *   activate accordions/tabs, so it doesn't re-trigger `accordion.js`'s/
 *   `collapse.js`'s own click-bound listeners (those stay scoped to real
 *   user clicks and don't race this controller's corrective scroll).
 * - Shadow-host discovery is scoped to the `mhecomm-` tag prefix (the only
 *   component family in this codebase using shadow DOM) instead of testing
 *   every element for `.shadowRoot`.
 * - `whenReady()` waiting is page-wide, not ancestor-scoped: a hash target
 *   is often a sibling of a `<mhecomm-article-cards>` component (e.g. a
 *   heading anchor next to a dynamically-loaded card list), not a
 *   descendant of it, so every `mhecomm-*` component with `whenReady()` is
 *   awaited regardless of DOM containment -- matching the original
 *   `article-cards.hash-scroll.ts` precedent.
 *
 * Scope (intentionally limited to what this codebase actually has):
 * accordions, tabs, `.collapse-deep-link` collapses, and `mhecomm-*` web
 * components exposing `whenReady(): Promise<void>`. No modal deep-link
 * target exists today, so modals are intentionally NOT handled.
 */
(function () {
  // Bounds only the Bootstrap activation chain (CSS-transition-bound, settles
  // in ms). Must NOT bound the whenReady() wait, which is network-bound but
  // guaranteed to eventually resolve -- capping it would silently drop the
  // corrective scroll for slow-loading components.
  const CHAIN_ACTIVATION_TIMEOUT_MS = 1500;
  // Safety-net ceiling per activation step, used only when the real
  // `shown.bs.*` event doesn't fire (e.g. reduced-motion skips the CSS
  // transition). Bootstrap's default fade transition is ~150ms, so this
  // stays a genuine fallback rather than a normal-case wait.
  const ACTIVATION_STEP_TIMEOUT_MS = 200;
  // Stability poll for the final scroll: how many times, and how far apart,
  // to re-check the target's position before scrolling. Only used for plain
  // Bootstrap-activated content (see `waitForStability` below).
  const STABILITY_POLL_MAX_ATTEMPTS = 5;
  const STABILITY_POLL_INTERVAL_MS = 400;
  // Fallback if the browser never fires `scrollend` (older browsers) -- long
  // enough for any reasonable smooth-scroll distance to finish.
  const SCROLLEND_FALLBACK_MS = 1000;
  const INTERACTION_EVENTS = ['wheel', 'touchmove', 'click', 'keydown', 'pointerdown'];

  let navigationGeneration = 0;
  let currentNavigation = null;
  let interactionCleanup = null;
  let lastTargetElementId = null;

  document.addEventListener('DOMContentLoaded', () => handleHashNavigation());
  window.addEventListener('popstate', () => handleHashNavigation());
  // Second pass: catches layout shifts from late-loading content (e.g.
  // images) inside an already-activated pane, which the first corrective
  // scroll (right after activation) can miss.
  window.addEventListener('load', () => attemptSecondCorrectiveScroll());

  function handleHashNavigation() {
    const navigation = { generation: ++navigationGeneration, userHasInteracted: false };
    currentNavigation = navigation;
    if (interactionCleanup) {
      interactionCleanup();
      interactionCleanup = null;
    }
    const targetElementId = window.location.hash.slice(1);
    if (!targetElementId) return;
    lastTargetElementId = targetElementId;

    const found = findElementDeep(targetElementId);
    const chain = found
      ? collectActivationChain(getDeepLinkElementsMap(found.root), targetElementId, found.element)
      : [];
    // Page-wide, not ancestor-scoped -- see "Why whenReady() waiting is
    // page-wide" above.
    const hostsToAwait = getAllMheComponents().filter(isDynamicComponent);

    // Always verify/correct the final position below, even when nothing
    // needs revealing -- native scroll-to-fragment is NOT guaranteed to run
    // on every hash-bearing load. On a page reload, the browser's scroll
    // RESTORATION (history.scrollRestoration = "auto" by default) can
    // restore the user's pre-reload scroll position instead of scrolling to
    // the fragment at all, and this controller previously had no way to
    // notice or correct that when the target required no activation.
    setupInteractionTracking(navigation);

    Promise.all([
      withTimeout(activateChain(chain, navigation), CHAIN_ACTIVATION_TIMEOUT_MS),
      Promise.allSettled(hostsToAwait.map((host) => host.whenReady())),
    ]).then(() => {
      if (!isNavigationCurrent(navigation)) return;
      // Re-resolve in case the target only rendered once `whenReady()` settled.
      const resolved = found || findElementDeep(targetElementId);
      // Only plain Bootstrap-activated content (a real chain) needs the
      // stability wait -- mhecomm-* web components already settled via
      // `whenReady()` above.
      if (resolved) attemptCorrectiveScroll(resolved.element, chain.length > 0, navigation);
    });
  }

  /** Re-checks the last hash target once the whole page (incl. images) has finished loading. */
  function attemptSecondCorrectiveScroll() {
    const navigation = currentNavigation;
    if (!lastTargetElementId || !isNavigationCurrent(navigation)) return;
    const resolved = findElementDeep(lastTargetElementId);
    if (resolved) attemptCorrectiveScroll(resolved.element, true, navigation);
  }

  // --- deep (shadow-DOM-aware) element lookup ---

  /**
   * Locates an element by id anywhere in the document, including inside
   * nested open shadow roots of `mhecomm-*` custom elements. Returns
   * `{ element, root }` (root = the Document/ShadowRoot directly containing
   * it), or `null` if it doesn't exist yet (e.g. still rendering async).
   * Closed shadow roots are opaque to JS and are skipped.
   */
  function findElementDeep(id, root = document) {
    const direct = root.querySelector(`#${CSS.escape(id)}`);
    if (direct) return { element: direct, root };

    for (const host of getMheComponents(root)) {
      if (!host.shadowRoot) continue; // no shadow root (yet), or closed -- nothing to descend into.
      const found = findElementDeep(id, host.shadowRoot);
      if (found) return found;
    }
    return null;
  }

  /** `mhecomm-*` custom elements directly under `root`. */
  function getMheComponents(root) {
    return Array.from(root.querySelectorAll('*')).filter((el) => el.tagName.indexOf('MHECOMM-') === 0);
  }

  /** Every `mhecomm-*` custom element anywhere in the document, at any shadow-nesting depth. */
  function getAllMheComponents(root = document, components = []) {
    getMheComponents(root).forEach((host) => {
      components.push(host);
      if (host.shadowRoot) getAllMheComponents(host.shadowRoot, components);
    });
    return components;
  }

  /** Components exposing `whenReady(): Promise<void>` -- must be awaited before scrolling. */
  function isDynamicComponent(el) {
    return typeof el.whenReady === 'function';
  }

  // --- discovery: same selectors/map shape as deep-link.js, scoped to a root ---

  function getDeepLinkElementsMap(root = document) {
    const deepLinkElementsMap = {};
    addTabs(deepLinkElementsMap, root);
    addAccordions(deepLinkElementsMap, root);
    addCollapse(deepLinkElementsMap, root);
    return deepLinkElementsMap;
  }

  function addTabs(map, root) {
    root.querySelectorAll('.nav-link').forEach((element) => {
      if (element.id) addElement(map, element.id, element);
    });
  }

  function addAccordions(map, root) {
    root.querySelectorAll('.accordion-header').forEach((element) => {
      const accordionButton = element.querySelector('.accordion-button');
      if (accordionButton) addElement(map, getAccordionId(element.id), accordionButton);
    });
  }

  function addCollapse(map, root) {
    root.querySelectorAll('.collapse-deep-link button[data-bs-toggle="collapse"]').forEach((element) => {
      if (element.id) addElement(map, element.id, element);
    });
  }

  /** Tabs collapse into accordions on mobile; "accordion-" prefix avoids id collisions. */
  function getAccordionId(id) {
    return id.startsWith('accordion-') ? id.slice(10) : id;
  }

  function addElement(map, id, element) {
    (map[id] || (map[id] = [])).push(element);
  }

  // --- activation chain collection (outside-in ordering) ---

  function collectActivationChain(map, targetElementId, resolvedTarget) {
    const directTargetElements = map[targetElementId] || [];
    const targetElements = directTargetElements.length ? directTargetElements : resolvedTarget ? [resolvedTarget] : [];
    if (!targetElements) return [];

    const chain = [];
    targetElements.forEach((targetElement) => {
      collectParents(targetElement, chain, new Set());
      if (directTargetElements.includes(targetElement) && !isOpen(targetElement)) chain.push(targetElement);
    });
    return chain;
  }

  function isOpen(element) {
    return element.getAttribute('aria-expanded') === 'true' || element.getAttribute('aria-selected') === 'true';
  }

  /** Walks actual target ancestry and collects Bootstrap controllers parents-first. */
  function collectParents(targetElement, chain, visited) {
    if (!targetElement) return;
    const container = targetElement.closest(
      '.tab-pane[id], .accordion-collapse[id], .collapse-deep-link .collapse[id]'
    );
    if (!container || visited.has(container)) return;
    visited.add(container);

    const controller = findBootstrapController(container);
    collectParents(container.parentElement, chain, visited);
    if (controller && !isOpen(controller)) chain.push(controller);
  }

  function findBootstrapController(container) {
    const root = container.getRootNode();
    const toggle = container.classList.contains('tab-pane') ? 'tab' : 'collapse';
    const triggers = root.querySelectorAll(`[data-bs-toggle="${toggle}"]`);
    return Array.from(triggers).find(
      (trigger) =>
        trigger.getAttribute('data-bs-target') === `#${container.id}` ||
        trigger.getAttribute('aria-controls') === container.id
    );
  }

  // --- activation via Bootstrap's own Collapse/Tab APIs ---

  function activateChain(chain, navigation) {
    // Ordered parents-first, so this activates outside-in.
    return chain.reduce(
      (promiseChain, element) => promiseChain.then(() => activateElement(element, navigation)),
      Promise.resolve()
    );
  }

  function activateElement(element, navigation) {
    if (!isNavigationCurrent(navigation)) return Promise.resolve();
    const isTab = element.classList.contains('nav-link');
    const targetSelector = element.getAttribute('data-bs-target');
    // Resolve within the element's own root (Document or ShadowRoot), so this
    // works when the activator/content pair live inside a shadow root.
    const collapseTarget = !isTab && targetSelector ? element.getRootNode().querySelector(targetSelector) : null;

    return new Promise((resolve) => {
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      const eventName = isTab ? 'shown.bs.tab' : 'shown.bs.collapse';
      (isTab ? element : collapseTarget || element).addEventListener(eventName, settle, { once: true });

      if (!isNavigationCurrent(navigation)) {
        settle();
      } else if (isTab && window.bootstrap && window.bootstrap.Tab) {
        window.bootstrap.Tab.getOrCreateInstance(element).show();
      } else if (!isTab && collapseTarget && window.bootstrap && window.bootstrap.Collapse) {
        window.bootstrap.Collapse.getOrCreateInstance(collapseTarget, { toggle: false }).show();
      } else {
        // Bootstrap unavailable or target unresolvable -- fall back to a real click.
        element.click();
      }

      // Safety net in case shown.bs.* never fires (e.g. reduced-motion skips the transition).
      setTimeout(settle, ACTIVATION_STEP_TIMEOUT_MS);
    });
  }

  // --- interaction guard (same contract as article-cards.hash-scroll.ts) ---

  function setupInteractionTracking(navigation) {
    const onInteraction = () => {
      navigation.userHasInteracted = true;
      cleanup();
    };
    const cleanup = () => {
      INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, onInteraction, { capture: true }));
      if (interactionCleanup === cleanup) interactionCleanup = null;
    };
    cleanup.navigation = navigation;
    INTERACTION_EVENTS.forEach((evt) => window.addEventListener(evt, onInteraction, { capture: true, passive: true }));
    interactionCleanup = cleanup;
  }

  function isNavigationCurrent(navigation) {
    return (
      navigation &&
      currentNavigation === navigation &&
      navigation.generation === navigationGeneration &&
      !navigation.userHasInteracted
    );
  }

  // --- corrective scroll + focus ---

  function attemptCorrectiveScroll(target, waitForStability, navigation) {
    if (!isNavigationCurrent(navigation)) return;

    applyScrollMarginOffset(target);

    // Listeners stay live until the promise below resolves -- i.e. until the
    // scroll (and any follow-up correction) has actually finished. Tearing
    // them down any earlier than that (e.g. right here, before scrolling
    // even starts) would leave a window where the user can scroll/click/etc.
    // while a scroll or correction is in flight without it being detected.
    const scrollPromise = waitForStability
      ? pollForStableLayout(target, STABILITY_POLL_MAX_ATTEMPTS, navigation).then((stableTarget) => {
          if (!isNavigationCurrent(navigation)) return undefined;
          return scrollAndFocus(stableTarget, navigation);
        })
      : scrollAndFocus(target, navigation);

    scrollPromise.then(() => {
      if (interactionCleanup && interactionCleanup.navigation === navigation) {
        interactionCleanup();
      }
    });
  }

  /**
   * `shown.bs.*` only signals the tab/accordion/collapse shell opened --
   * content inside it (e.g. late-loading images) can keep shifting the
   * target's position afterwards. Re-checks the target's position up to
   * `maxAttempts` times, `STABILITY_POLL_INTERVAL_MS` apart, and resolves as
   * soon as two consecutive checks agree (most content is already stable on
   * the first check, so this exits early rather than always waiting out the
   * full ceiling). `maxAttempts` is a ceiling for the rare still-shifting
   * case, not a guaranteed wait.
   */
  function pollForStableLayout(target, maxAttempts, navigation) {
    return new Promise((resolve) => {
      let lastTop = target.getBoundingClientRect().top;

      function check(attemptsLeft) {
        if (!isNavigationCurrent(navigation) || attemptsLeft <= 0) {
          resolve(target);
          return;
        }
        setTimeout(() => {
          if (!isNavigationCurrent(navigation)) {
            resolve(target);
            return;
          }
          const currentTop = target.getBoundingClientRect().top;
          if (currentTop === lastTop) {
            resolve(target);
          } else {
            lastTop = currentTop;
            check(attemptsLeft - 1);
          }
        }, STABILITY_POLL_INTERVAL_MS);
      }

      check(maxAttempts);
    });
  }

  /** Returns a promise that resolves once scrolling (and any follow-up correction) is fully done. */
  function scrollAndFocus(target, navigation) {
    if (!isNavigationCurrent(navigation)) return Promise.resolve();
    if (!needsFurtherCorrection(target)) {
      if (isNavigationCurrent(navigation)) focusTarget(target);
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      requestAnimationFrame(() => {
        if (!isNavigationCurrent(navigation)) {
          resolve();
          return;
        }
        if (prefersReducedMotion) {
          target.scrollIntoView({ behavior: 'auto', block: 'start' });
          if (isNavigationCurrent(navigation)) focusTarget(target);
          resolve();
        } else {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          if (isNavigationCurrent(navigation)) focusTarget(target);
          snapToFinalPosition(target, navigation).then(resolve);
        }
      });
    });
  }

  /**
   * The animated (`smooth`) scroll above can be knocked off course mid-flight
   * by unrelated page activity (e.g. a third-party cookie-consent widget
   * shifting layout) and settle short of its own destination. Once the
   * animation reports done (`scrollend`, with a fallback timeout in case it
   * never fires), only correct if it's still meaningfully off -- small
   * rounding/sub-pixel differences are normal and correcting those
   * unconditionally is what made the end of every scroll feel like a little
   * jump instead of landing smoothly. The correction itself also animates
   * (`behavior: 'smooth'`) rather than teleporting instantly: the first
   * animation has already fully stopped by the time this runs, so this is a
   * second, separate glide toward the real target, not an interruption --
   * that reads as "kept gliding a bit further" instead of a jarring snap.
   */
  /** Returns a promise that resolves once the correction (if any was needed) has also finished. */
  function snapToFinalPosition(target, navigation) {
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.removeEventListener('scrollend', finish);
        if (isNavigationCurrent(navigation) && needsFurtherCorrection(target)) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Wait out the correction's own animation too, so listeners stay
          // live for its full duration -- not just the first scroll's.
          window.addEventListener('scrollend', resolve, { once: true });
          setTimeout(resolve, SCROLLEND_FALLBACK_MS);
        } else {
          resolve();
        }
      };
      window.addEventListener('scrollend', finish, { once: true });
      setTimeout(finish, SCROLLEND_FALLBACK_MS);
    });
  }

  /** Tolerance for `needsFurtherCorrection` -- bigger than normal rounding/layout jitter, smaller than a real undershoot. */
  const SNAP_CORRECTION_TOLERANCE_PX = 24;

  function needsFurtherCorrection(target) {
    const expectedTop = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    const { top } = target.getBoundingClientRect();
    return Math.abs(top - expectedTop) > SNAP_CORRECTION_TOLERANCE_PX;
  }

  /** Same offset var as anchorScrollHandler.js used, applied via scroll-margin-top instead of manual scrollTo math. */
  function applyScrollMarginOffset(target) {
    const navHeight = getComputedStyle(document.documentElement).getPropertyValue(
      '--theme-corporate-navbar-container-height'
    );
    if (navHeight && !target.style.scrollMarginTop) {
      target.style.scrollMarginTop = navHeight;
    }
  }

  function focusTarget(target) {
    if (!target.hasAttribute('tabindex') && !isNativelyFocusable(target)) {
      target.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: true });
  }

  function isNativelyFocusable(element) {
    return /^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(element.tagName) || element.hasAttribute('href');
  }

  function withTimeout(promise, ms) {
    return Promise.race([promise, new Promise((resolve) => setTimeout(resolve, ms))]);
  }
})();
